/**
 * 权限节点编辑表单 (Drawer)
 * 与后端 foundation-service /permissions API 对齐
 */
import React, { useEffect, useState } from 'react';
import {
  Drawer, Form, Input, InputNumber, Radio, Select, TreeSelect,
  Button, Space, message, Spin,
} from 'antd';
import { createPermission, updatePermission } from '../../api/permission';
import type { Permission, PermissionFormData } from '../../api/types';
import { PermissionType, PermissionStatus } from '../../api/types';

interface TreeSelectNode {
  value: string;
  title: string;
  label: string;
  children?: TreeSelectNode[];
}

interface PermissionFormProps {
  visible: boolean;
  permission: Permission | null;
  parentId?: string;
  permissionTree: Permission[];
  onClose: () => void;
  onSuccess: () => void;
}

/** Convert Permission tree to TreeSelect data structure */
const convertToTreeSelectData = (
  permissions: Permission[],
  excludeId?: string,
): TreeSelectNode[] => {
  return permissions
    .filter((p) => p.id !== excludeId)
    .map((perm) => ({
      value: perm.id,
      title: perm.name,
      label: perm.name,
      children: perm.children
        ? convertToTreeSelectData(perm.children, excludeId)
        : undefined,
    }));
};

/** HTTP 方法选项 */
const HTTP_METHOD_OPTIONS = [
  { label: 'GET', value: 'GET' },
  { label: 'POST', value: 'POST' },
  { label: 'PUT', value: 'PUT' },
  { label: 'DELETE', value: 'DELETE' },
  { label: 'PATCH', value: 'PATCH' },
];

const PermissionForm: React.FC<PermissionFormProps> = ({
  visible,
  permission,
  parentId,
  permissionTree,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!permission;

  const [currentType, setCurrentType] = useState<PermissionType>(PermissionType.DIRECTORY);

  useEffect(() => {
    if (visible) {
      if (permission) {
        form.setFieldsValue({
          parentId: permission.parentId || undefined,
          name: permission.name,
          code: permission.code,
          type: permission.menuType,
          sort: permission.sort ?? 0,
          status: permission.status,
          path: permission.path || undefined,
          method: (permission as any).method || undefined,
          description: permission.description || undefined,
        });
        setCurrentType(permission.menuType);
      } else {
        form.resetFields();
        form.setFieldsValue({
          parentId: parentId || undefined,
          type: PermissionType.DIRECTORY,
          sort: 0,
          status: PermissionStatus.NORMAL,
        });
        setCurrentType(PermissionType.DIRECTORY);
      }
    }
  }, [visible, permission, parentId, form]);

  /** Handle form submission */
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const data: PermissionFormData & { method?: string } = {
        code: values.code,
        name: values.name,
        type: values.type,
        parentId: values.parentId || undefined,
        level: 0,
        sort: values.sort ?? 0,
        status: values.status,
        description: values.description || undefined,
        path: values.path || undefined,
        method: values.method || undefined,
      };

      if (isEdit) {
        await updatePermission(permission!.id, data);
        message.success('更新成功');
      } else {
        await createPermission(data);
        message.success('创建成功');
      }

      onSuccess();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      console.error('提交失败', error);
      message.error('操作失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  const treeSelectData = convertToTreeSelectData(
    permissionTree,
    permission?.id,
  );

  /** Whether to show path field (directory and menu need route path, API needs endpoint path) */
  const showPathField = currentType !== PermissionType.BUTTON;

  /** Whether to show method field (only for API-type permissions mapped to BUTTON with path) */
  const isApiType = currentType === PermissionType.BUTTON;

  return (
    <Drawer
      title={isEdit ? '编辑权限' : '新增权限'}
      open={visible}
      onClose={onClose}
      width={560}
      destroyOnHidden
      footer={
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={onClose}>取消</Button>
            <Button type="primary" onClick={handleSubmit} loading={submitting}>
              {isEdit ? '更新' : '创建'}
            </Button>
          </Space>
        </div>
      }
    >
      <Spin spinning={submitting}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            type: PermissionType.DIRECTORY,
            sort: 0,
            status: PermissionStatus.NORMAL,
          }}
        >
          {/* Parent node */}
          <Form.Item
            name="parentId"
            label="上级权限"
          >
            <TreeSelect
              placeholder="请选择上级权限（不选则为顶级节点）"
              treeData={treeSelectData}
              allowClear
              treeLine={{ showLeafIcon: false }}
              treeDefaultExpandAll
              showSearch
              treeNodeFilterProp="title"
            />
          </Form.Item>

          {/* Permission type */}
          <Form.Item
            name="type"
            label="权限类型"
            rules={[{ required: true, message: '请选择权限类型' }]}
          >
            <Radio.Group
              onChange={(e) => setCurrentType(e.target.value)}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value={PermissionType.DIRECTORY}>目录</Radio.Button>
              <Radio.Button value={PermissionType.MENU}>菜单</Radio.Button>
              <Radio.Button value={PermissionType.BUTTON}>按钮/API</Radio.Button>
            </Radio.Group>
          </Form.Item>

          {/* Name */}
          <Form.Item
            name="name"
            label="权限名称"
            rules={[
              { required: true, message: '请输入权限名称' },
              { max: 50, message: '权限名称不能超过50个字符' },
            ]}
          >
            <Input placeholder="请输入权限名称" maxLength={50} />
          </Form.Item>

          {/* Code */}
          <Form.Item
            name="code"
            label="权限编码"
            rules={[
              { required: true, message: '请输入权限编码' },
              {
                pattern: /^[A-Za-z0-9_:.-]+$/,
                message: '只能包含字母、数字、下划线、冒号、点和连字符',
              },
            ]}
          >
            <Input
              placeholder="请输入权限编码，如 system:user:list"
              maxLength={100}
              disabled={isEdit}
            />
          </Form.Item>

          {/* Sort */}
          <Form.Item
            name="sort"
            label="排序"
          >
            <InputNumber
              min={0}
              max={9999}
              placeholder="请输入排序号"
              style={{ width: '100%' }}
            />
          </Form.Item>

          {/* Status */}
          <Form.Item
            name="status"
            label="状态"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Radio.Group>
              <Radio value={PermissionStatus.NORMAL}>启用</Radio>
              <Radio value={PermissionStatus.DISABLED}>禁用</Radio>
            </Radio.Group>
          </Form.Item>

          {/* Path (for directory/menu = route path, for API = endpoint path) */}
          {showPathField && (
            <Form.Item
              name="path"
              label={isApiType ? 'API 路径' : '路由路径'}
            >
              <Input
                placeholder={isApiType ? '如 /api/v1/users' : '如 /system/user'}
                maxLength={200}
              />
            </Form.Item>
          )}

          {/* HTTP Method (for button/API type) */}
          {isApiType && (
            <Form.Item
              name="method"
              label="HTTP 方法"
            >
              <Select
                placeholder="请选择 HTTP 方法（按钮权限可不填）"
                allowClear
                options={HTTP_METHOD_OPTIONS}
              />
            </Form.Item>
          )}

          {/* Description */}
          <Form.Item
            name="description"
            label="描述"
          >
            <Input.TextArea
              placeholder="请输入权限描述"
              rows={3}
              maxLength={200}
              showCount
            />
          </Form.Item>
        </Form>
      </Spin>
    </Drawer>
  );
};

export default PermissionForm;
